import autoComplete from '@tarekraafat/autocomplete.js';

export interface IAutocompleteOptions {
    input: HTMLInputElement,
    panelContainer: HTMLElement,
    resultsContainer: HTMLElement,
    blockContainer: HTMLElement,
    keys: string[],
    autocompleteSelector: string,
    autocompleteResultsSelector: string,
    resultFormat: (data: any, source: any) => void,
    sortFunction: (a: any, b: any) => number,
    dataSrc: any,
    cache: boolean,
    form?: HTMLFormElement,
    submitCallback?: any,
    clickCallback?: any,
    closeButton?: HTMLButtonElement,
    uniqueId?: string,
    noResultsMsg?: string
}

export class AutocompleteComponent {
    options: IAutocompleteOptions;

    constructor(options: IAutocompleteOptions) {
        this.options = options;
        this.options.uniqueId = this.options.uniqueId ? this.options.uniqueId : `${Math.random() * 100}`;
        this.init();
    }

    init() {
        this.options.input.removeAttribute('disabled');
        new autoComplete(this.generateOptions());
        this.options.panelContainer.classList.remove('loading-container');
        if (this.options.closeButton) {
            this.options.closeButton.addEventListener('click', () => {
                this.options.input.value = '';
                this.triggerAutocompleteUpdate(this.options.input);
                this.options.input.focus();
            });
        }
        this.options.blockContainer.addEventListener('keyup', (e) => {
            if (e.key === 'Escape') {
                this.options.input.value = '';
                this.triggerAutocompleteUpdate(this.options.input);
            }
        });

        // Update when autocomplete renders. v10 emits 'results' with the feedback object as
        // detail; it also maintains aria-expanded on the input itself, so we only own the
        // .has-results class here. (v7's 'autoComplete' event no longer exists.)
        this.options.input.addEventListener('results', (event) => {
            if ((event['detail']['query'] as string).length > 1) {
                this.options.blockContainer.classList.add('has-results');
            } else {
                this.options.blockContainer.classList.remove('has-results');
            }
        });

        // Down arrow from the input is wired through config.events.input.keydown so that it
        // replaces v10's internal navigation rather than competing with it.


        document.addEventListener("click", e => {
            if (!(e.target as HTMLElement)?.closest(".autocomplete")) {
                document.querySelector(this.options.autocompleteResultsSelector)?.classList.add("hide");
            }
        })

        this.options.input.addEventListener('focus', (e) => {
            setTimeout(e => {
                document.querySelector(this.options.autocompleteResultsSelector)?.classList.remove("hide");
            })
        })

        // Handle up/down arrows inside list
        this.options.resultsContainer.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowDown') {
                const curItem = document.activeElement.parentElement;
                if (curItem) {
                    e.preventDefault();
                    const nextItem = curItem.nextElementSibling;
                    if (nextItem) {
                        const nextLink = nextItem.querySelector('a, button') as HTMLAnchorElement | HTMLButtonElement;
                        if (nextLink) {
                            nextLink.focus();
                        }
                    }
                }
            } else if (e.key === 'ArrowUp') {
                const curItem = document.activeElement.parentElement;
                if (curItem) {
                    e.preventDefault();
                    if (curItem.matches('li:first-child, .autocomplete-result:first-child, a.autocomplete-result-link:first-child')) {
                        this.options.input.focus();
                    } else {
                        const prevItem = curItem.previousElementSibling;
                        if (prevItem) {
                            const prevLink = prevItem.querySelector('a, button') as HTMLAnchorElement | HTMLButtonElement;
                            if (prevLink) {
                                prevLink.focus();
                            }
                        }
                    }
                }
            } else if (e.key === 'Tab') {
                const curItem = document.activeElement;
                if (curItem && curItem.hasAttribute('data-query')) {
                    this.options.input.value = curItem.getAttribute('data-query');
                    this.triggerAutocompleteUpdate(this.options.input);
                }
            }
        });

        if (this.options.submitCallback && this.options.form) {
            // Handle form submission
            this.options.form.addEventListener('submit', (e) => {
                e.preventDefault();
                this.options.submitCallback();
            })
        }

        if (this.options.clickCallback) {
            this.options.blockContainer.addEventListener('click', (e) => {
                if (e.target['classList'].contains('autoComplete_highlighted')) {
                    this.options.clickCallback(e.target['parentElement']);
                } else {
                    this.options.clickCallback(e.target);
                }
            })
        }
    }

    triggerAutocompleteUpdate(input: HTMLInputElement) {
        input.dispatchEvent(new Event('input', {bubbles: true}));
    }

    generateOptions() {
        return {
            selector: this.options.autocompleteSelector,
            // v10 wraps the input in a div.autoComplete_wrapper by default, which breaks the
            // absolute positioning of .autocomplete-results-panel. Opting out keeps the markup
            // flat; v10 still applies the combobox ARIA attributes directly to the input.
            wrapper: false,
            data: {
                src: this.options.dataSrc,
                keys: this.options.keys,
                cache: this.options.cache,
                // Replaces v7's top-level `sort`. Applied before the maxResults slice.
                filter: (matches) => this.options.sortFunction
                    ? [...matches].sort((a, b) => this.options.sortFunction(a, b))
                    : matches
            },
            debounce: 100,
            threshold: 1,
            resultItem: {
                tag: 'div',
                // The string form adds this class to the generated <mark>, which keeps the
                // existing SCSS rule, the clickCallback target check below, and the
                // `match.indexOf('<') === 0` prefix ordering in the school sorts all working.
                highlight: 'autoComplete_highlighted',
                // v10 passes (element, data) — the reverse of v7's content(data, source).
                element: (item, data) => {
                    this.options.resultFormat(data, item);
                }
            },
            resultsList: {
                // Must be a selector string or a function; v10 calls anything non-string.
                destination: this.options.autocompleteResultsSelector,
                position: 'afterbegin',
                tag: 'div',
                class: 'autocomplete-results',
                id: `LIST_${this.options.uniqueId}`,
                maxResults: 1000,
                // Now a boolean: render the list even with zero matches so the
                // "no results" message below has somewhere to go.
                noResults: true,
                element: (list, data) => {
                    list.setAttribute('aria-label', `Search Results`);
                    if (!data.matches.length) {
                        const result = document.createElement("div");
                        result.setAttribute('class', 'autocomplete-result');
                        result.innerHTML = this.options.noResultsMsg ? `${this.options.noResultsMsg.replace(/{{term}}/, this.options.input.value)}` : "No Results";
                        list.appendChild(result);
                    }
                }
            },
            events: {
                input: {
                    // Supplying these suppresses v10's own keydown/blur handlers, whose
                    // internal aria-selected cursor would fight the real-DOM-focus
                    // navigation this class implements.
                    keydown: (e) => this.handleInputKeydown(e),
                    blur: () => { /* closing is handled by the document click handler below */ }
                }
            }
        }
    }

    handleInputKeydown(e: KeyboardEvent) {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            // v10 maintains aria-expanded on the input itself when wrapper is disabled.
            if (this.options.input.getAttribute('aria-expanded') == 'true') {
                const firstLink = this.options.resultsContainer.querySelector('li:first-child a, li:first-child button, .autocomplete-result:first-child a, .autocomplete-result:first-child button, a.autocomplete-result-link:first-child') as HTMLAnchorElement | HTMLButtonElement;
                if (firstLink) {
                    firstLink.focus();
                }
            }
        }
    }
}